import React from 'react';
import styled from 'astroturf/react';
import classnames from 'classnames';

import 'app/assets/stylesheets/design_system/_grid.scss';
import _GridContainer from "Grid-GridContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/grid/Grid.tsx?styleId=GridContainer";


type GridColumnProps = React.PropsWithChildren<{
  sm: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  md?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  lg?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  subgrid?: boolean;
}> &
  React.HTMLAttributes<HTMLDivElement>;

/**
 * Components for full-width page layouts using a responsive 12 (or 6)-column grid system.
 * The default Grid container stretches to fit on small and medium, but is confined to a fixed
 * width on Large. This default grid is intended for use on most types of pages, where the
 * content should be constrained to a maximum width for usability and legibility.
 * (The content columns' effective maximum width is 1128px.)
 *
 * The Grid also permits customizing the stretch/fixed behavior at each breakpoint. For example,
 * you can allow the Large breakpoint to stretch. Use of this "wide" behavior is
 * intended for use on dense pages with multiple columns, where no content spans the full
 * width of the page and legibility is not a concern.
 *
 * The breakpoints are:
 * - Small (6 columns): 0px - 567px
 * - Medium (6 columns): 568px - 1279px
 * - Large (12 columns): 1280px and up
 *
 * A layout consists of one Grid component, which contains one or more GridColumn components
 * as direct children. For each GridColumn, you must specify the number of columns it should
 * occupy at each breakpoint, or 0 to hide the column at that breakpoint. For example, to have
 * a column be hidden in Small, span 1 column in Medium, and span 2 columns in Large:
 *
 * ```
 * <GridColumn sm={0} md={1} lg={2} />
 * ```
 *
 * You can leave out the `md` span to have it default to the value of `sm`, or leave out `lg`
 * to default to the relative proportion of `md` (or `sm` if `md` is also unspecified). So,
 * missing values take the last specified value's relative proportion. For instance, these
 * two usages are equivalent:
 *
 * ```
 * <GridColumn sm={2} />
 * <GridColumn sm={2} md={2} lg={4} />
 * ```
 *
 * As are these two:
 *
 * ```
 * <GridColumn sm={0} lg={1} />
 * <GridColumn sm={0} md={0} lg={1} />
 * ```
 *
 * Columns can also be grid containers themselves, to created nested grid layouts with
 * grandchildren split across different columns. To enable this, set the `subgrid` boolean
 * property:
 *
 * ```
 * <GridColumn subgrid sm={0} md={2}>
 *   <GridColumn sm={0} md={1}>Sub-column 1</GridColumn>
 *   <GridColumn sm={0} md={1}>Sub-column 2</GridColumn>
 * </GridColumn>
 * ```
 *
 * Subgrid columns will only have the number of columns available that they span in total -
 * for instance, the above example only has 2 columns to split between its own children.
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { Grid, GridColumn } from 'js/design_system/grid/Grid';
 *
 * <Grid>
 *   <GridColumn sm={0} md={2} lg={2}>Left column</GridColumn>
 *   <GridColumn sm={2} md={4} lg={8}>Center column</GridColumn>
 *   <GridColumn sm={0} md={2} lg={2}>Right column</GridColumn>
 * </Grid>
 * ```
 *
 * ## Wide Grid, stretches on all breakpoints
 *
 * @example
 *
 * ```tsx
 * import { Grid, GridColumn } from 'js/design_system/grid/Grid';
 *
 * <Grid stretchSmall={true} stretchMedium={true} stretchLarge={true}>
 *    <GridColumn sm={6} md={6} lg={12}>Page Content</GridColumn>
 * </Grid>
 * ```
 *
 * ## Fixed Grid, has maximum width on all breakpoints
 *
 * @example
 *
 * ```tsx
 * import { Grid, GridColumn } from 'js/design_system/grid/Grid';
 *
 * <Grid stretchSmall={false} stretchMedium={false} stretchLarge={false}>
 *    <GridColumn sm={6} md={6} lg={12}>Page Content</GridColumn>
 * </Grid>
 * ```
 */
const GridColumn: React.FC<GridColumnProps> = ({
  children,
  className,
  sm: smallSpan,
  md,
  lg,
  subgrid,
  ...props
}) => {
  // Default to proportions of small (mobile) breakpoint if unspecified
  const mediumSpan = typeof md === 'number' ? md : smallSpan;

  // Default to twice medium's proportions (jumping from 6 to 12 columns),
  // which itself might just be small's.
  const largeSpan = typeof lg === 'number' ? lg : mediumSpan * 2;

  const smClass = `ds-grid-column-small-${
    smallSpan === 0 ? 'hidden' : String(smallSpan)
  }`;
  const mdClass = `ds-grid-column-medium-${
    mediumSpan === 0 ? 'hidden' : String(mediumSpan)
  }`;
  const lgClass = `ds-grid-column-large-${
    largeSpan === 0 ? 'hidden' : String(largeSpan)
  }`;

  return (
    <div
      className={classnames(className, smClass, mdClass, lgClass, {
        'ds-grid-subgrid': Boolean(subgrid),
      })}
      {...props}
    >
      {children}
    </div>
  );
};

export type BaseGridProps = {
  stretchSmall?: boolean;
  stretchMedium?: boolean;
  stretchLarge?: boolean;
};

export type GridProps = BaseGridProps & React.HTMLAttributes<HTMLDivElement>;

const GridContainer = /*#__PURE__*/styled("div", null, {
  displayName: "GridContainer",
  styles: _GridContainer
});

/**
 * Components for full-width page layouts using a responsive 12 (or 6)-column grid system.
 * The default Grid container stretches to fit on small and medium, but is confined to a fixed
 * width on Large. This default grid is intended for use on most types of pages, where the
 * content should be constrained to a maximum width for usability and legibility.
 * (The content columns' effective maximum width is 1128px.)
 *
 * The Grid also permits customizing the stretch/fixed behavior at each breakpoint. For example,
 * you can allow the Large breakpoint to stretch. Use of this "wide" behavior is
 * intended for use on dense pages with multiple columns, where no content spans the full
 * width of the page and legibility is not a concern.
 *
 * The breakpoints are:
 * - Small (6 columns): 0px - 567px
 * - Medium (6 columns): 568px - 1279px
 * - Large (12 columns): 1280px and up
 *
 * A layout consists of one Grid component, which contains one or more GridColumn components
 * as direct children. For each GridColumn, you must specify the number of columns it should
 * occupy at each breakpoint, or 0 to hide the column at that breakpoint. For example, to have
 * a column be hidden in Small, span 1 column in Medium, and span 2 columns in Large:
 *
 * ```
 * <GridColumn sm={0} md={1} lg={2} />
 * ```
 *
 * You can leave out the `md` span to have it default to the value of `sm`, or leave out `lg`
 * to default to the relative proportion of `md` (or `sm` if `md` is also unspecified). So,
 * missing values take the last specified value's relative proportion. For instance, these
 * two usages are equivalent:
 *
 * ```
 * <GridColumn sm={2} />
 * <GridColumn sm={2} md={2} lg={4} />
 * ```
 *
 * As are these two:
 *
 * ```
 * <GridColumn sm={0} lg={1} />
 * <GridColumn sm={0} md={0} lg={1} />
 * ```
 *
 * Columns can also be grid containers themselves, to created nested grid layouts with
 * grandchildren split across different columns. To enable this, set the `subgrid` boolean
 * property:
 *
 * ```
 * <GridColumn subgrid sm={0} md={2}>
 *   <GridColumn sm={0} md={1}>Sub-column 1</GridColumn>
 *   <GridColumn sm={0} md={1}>Sub-column 2</GridColumn>
 * </GridColumn>
 * ```
 *
 * Subgrid columns will only have the number of columns available that they span in total -
 * for instance, the above example only has 2 columns to split between its own children.
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { Grid, GridColumn } from 'js/design_system/grid/Grid';
 *
 * <Grid>
 *   <GridColumn sm={0} md={2} lg={2}>Left column</GridColumn>
 *   <GridColumn sm={2} md={4} lg={8}>Center column</GridColumn>
 *   <GridColumn sm={0} md={2} lg={2}>Right column</GridColumn>
 * </Grid>
 * ```
 *
 * ## Wide Grid, stretches on all breakpoints
 *
 * @example
 *
 * ```tsx
 * import { Grid, GridColumn } from 'js/design_system/grid/Grid';
 *
 * <Grid stretchSmall={true} stretchMedium={true} stretchLarge={true}>
 *    <GridColumn sm={6} md={6} lg={12}>Page Content</GridColumn>
 * </Grid>
 * ```
 *
 * ## Fixed Grid, has maximum width on all breakpoints
 *
 * @example
 *
 * ```tsx
 * import { Grid, GridColumn } from 'js/design_system/grid/Grid';
 *
 * <Grid stretchSmall={false} stretchMedium={false} stretchLarge={false}>
 *    <GridColumn sm={6} md={6} lg={12}>Page Content</GridColumn>
 * </Grid>
 * ```
 */
const Grid: React.FC<GridProps> = ({
  stretchSmall = true,
  stretchMedium = true,
  stretchLarge = false,
  children,
  className,
  ...props
}) => {
  return (
    <GridContainer
      stretchSmall={stretchSmall}
      stretchMedium={stretchMedium}
      stretchLarge={stretchLarge}
      className={className}
      {...props}
    >
      {children}
    </GridContainer>
  );
};

export { Grid, GridColumn };
